import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, X, Loader2, Film, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, videoUrl, fileUrl, uploadVideoChunked, formatApiErrorDetail } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const EMPTY = {
  title: "",
  tag: "Thought Leadership",
  summary: "",
  content_type: "internal",
  body: "",
  external_url: "",
  cover_image_id: null,
  video_file_id: null,
  resource_file_id: null,
  resource_filename: "",
  status: "draft",
};

const CONTENT_TYPES = [
  { key: "internal", label: "On-site article" },
  { key: "external", label: "External link" },
  { key: "video", label: "Video" },
  { key: "resource", label: "Resource (PDF)" },
];

export default function ArticleEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const resourceRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(null);
  const [resourceUploading, setResourceUploading] = useState(false);

  useDocumentMeta({ title: isEdit ? "Edit Article" : "New Article", path: "/admin" });

  const loadArticle = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/insights/${id}`);
      setForm({ ...EMPTY, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load article");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) loadArticle();
  }, [isEdit, loadArticle]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/api/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("cover_image_id", data.file_id);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoProgress(0);
    try {
      const data = await uploadVideoChunked(file, setVideoProgress);
      set("video_file_id", data.file_id);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed");
    } finally {
      setVideoProgress(null);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const handleResourceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResourceUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/api/admin/upload-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, resource_file_id: data.file_id, resource_filename: data.filename }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setResourceUploading(false);
      if (resourceRef.current) resourceRef.current.value = "";
    }
  };

  const save = async (statusOverride) => {
    const payload = { ...form, status: statusOverride || form.status };
    if (!payload.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (payload.content_type === "external" && !payload.external_url.trim()) {
      toast.error("External articles need a link URL");
      return;
    }
    if (payload.content_type === "internal" && !payload.body.trim()) {
      toast.error("On-site articles need body content");
      return;
    }
    if (payload.content_type === "video" && !payload.video_file_id) {
      toast.error("Please upload a video first");
      return;
    }
    if (payload.content_type === "resource" && !payload.resource_file_id) {
      toast.error("Please upload a file first");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/admin/insights/${id}`, payload);
      else await api.post("/api/admin/insights", payload);
      toast.success(isEdit ? "Article updated" : "Article created");
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F1EE] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-charcoal/40 animate-spin" />
      </div>
    );
  }

  const inputCls =
    "w-full bg-canvas border border-charcoal/20 px-4 py-3.5 font-serif text-base text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-forest transition-colors duration-300";
  const labelCls =
    "block font-sans text-[11px] tracking-[0.2em] uppercase text-charcoal/50 mb-3";

  return (
    <div data-testid="article-editor" className="min-h-screen bg-[#F1F1EE]">
      <header className="bg-charcoal text-canvas">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link
            to="/admin"
            data-testid="back-to-dashboard"
            className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.16em] uppercase text-canvas/60 hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} />
            Back
          </Link>
          <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-gold">
            {isEdit ? "Edit Article" : "New Article"}
          </span>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 lg:px-10 py-14 space-y-8">
        {/* Title */}
        <div>
          <label className={labelCls}>Title</label>
          <input
            data-testid="editor-title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            className={inputCls}
            placeholder="Article title"
          />
        </div>

        {/* Tag + status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Tag / Category</label>
            <input
              data-testid="editor-tag"
              value={form.tag}
              onChange={(e) => set("tag", e.target.value)}
              className={inputCls}
              placeholder="Thought Leadership"
            />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select
              data-testid="editor-status"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              className={inputCls}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        {/* Summary */}
        <div>
          <label className={labelCls}>Summary / Excerpt</label>
          <textarea
            data-testid="editor-summary"
            value={form.summary}
            onChange={(e) => set("summary", e.target.value)}
            rows={2}
            className={inputCls}
            placeholder="A short teaser shown on the Insights grid."
          />
        </div>

        {/* Cover image */}
        <div>
          <label className={labelCls}>Cover Image</label>
          <div className="flex items-center gap-5">
            <div className="w-40 h-24 bg-[#E4E4DE] border border-charcoal/15 overflow-hidden flex items-center justify-center shrink-0">
              {form.cover_image_id ? (
                <img src={imageUrl(form.cover_image_id)} alt="cover" className="w-full h-full object-cover" />
              ) : (
                <span className="font-sans text-[10px] tracking-[0.16em] uppercase text-charcoal/35">
                  No image
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="upload-image-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.14em] uppercase font-bold px-5 py-3 bg-charcoal text-canvas hover:bg-forest transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.6} />}
                {uploading ? "Uploading…" : "Upload"}
              </button>
              {form.cover_image_id && (
                <button
                  type="button"
                  data-testid="remove-image-button"
                  onClick={() => set("cover_image_id", null)}
                  className="inline-flex items-center gap-1.5 font-sans text-[11px] tracking-[0.14em] uppercase text-charcoal/50 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" strokeWidth={1.6} />
                  Remove
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleUpload}
                className="hidden"
                data-testid="image-file-input"
              />
            </div>
          </div>
        </div>

        {/* Content type toggle */}
        <div>
          <label className={labelCls}>Content Type</label>
          <div className="flex flex-wrap gap-px bg-charcoal/15 border border-charcoal/15 w-fit">
            {CONTENT_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`content-type-${t.key}`}
                onClick={() => set("content_type", t.key)}
                className={`font-sans text-[11px] tracking-[0.14em] uppercase px-6 py-3 transition-colors ${
                  form.content_type === t.key ? "bg-forest text-canvas" : "bg-canvas text-charcoal/60 hover:text-charcoal"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Type-specific fields */}
        {form.content_type === "internal" && (
          <div>
            <label className={labelCls}>Body (Markdown supported)</label>
            <textarea
              data-testid="editor-body"
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              rows={16}
              className={`${inputCls} font-mono text-sm leading-relaxed`}
              placeholder={"## Heading\n\nWrite your article here. **Bold**, _italic_, and [links](https://example.com) are supported."}
            />
          </div>
        )}

        {form.content_type === "external" && (
          <div>
            <label className={labelCls}>External URL</label>
            <input
              data-testid="editor-external-url"
              value={form.external_url}
              onChange={(e) => set("external_url", e.target.value)}
              className={inputCls}
              placeholder="https://www.amadi.io/thought-leadership/..."
            />
          </div>
        )}

        {form.content_type === "video" && (
          <div>
            <label className={labelCls}>Video File (MP4, WEBM, MOV — up to 100 MB)</label>
            {form.video_file_id ? (
              <div className="space-y-4">
                <video
                  data-testid="editor-video-preview"
                  key={form.video_file_id}
                  src={videoUrl(form.video_file_id)}
                  controls
                  className="w-full max-w-xl aspect-video bg-charcoal border border-charcoal/15"
                />
                <button
                  type="button"
                  data-testid="remove-video-button"
                  onClick={() => set("video_file_id", null)}
                  className="inline-flex items-center gap-1.5 font-sans text-[11px] tracking-[0.14em] uppercase text-charcoal/50 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" strokeWidth={1.6} />
                  Replace video
                </button>
              </div>
            ) : (
              <div>
                <button
                  type="button"
                  data-testid="upload-video-button"
                  onClick={() => videoRef.current?.click()}
                  disabled={videoProgress !== null}
                  className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.14em] uppercase font-bold px-6 py-4 bg-charcoal text-canvas hover:bg-forest transition-colors disabled:opacity-60"
                >
                  {videoProgress !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" strokeWidth={1.6} />}
                  {videoProgress !== null ? `Uploading ${videoProgress}%` : "Upload Video"}
                </button>
                {videoProgress !== null && (
                  <div className="mt-3 h-1.5 w-full max-w-xl bg-charcoal/10 overflow-hidden">
                    <div className="h-full bg-forest transition-all duration-200" style={{ width: `${videoProgress}%` }} />
                  </div>
                )}
              </div>
            )}
            <input
              ref={videoRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-m4v,video/ogg"
              onChange={handleVideoUpload}
              className="hidden"
              data-testid="video-file-input"
            />
          </div>
        )}

        {form.content_type === "resource" && (
          <div>
            <label className={labelCls}>Downloadable File (PDF, DOC, PPT, XLS — up to 25 MB)</label>
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="button"
                data-testid="upload-resource-button"
                onClick={() => resourceRef.current?.click()}
                disabled={resourceUploading}
                className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.14em] uppercase font-bold px-6 py-4 bg-charcoal text-canvas hover:bg-forest transition-colors disabled:opacity-60"
              >
                {resourceUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.6} />}
                {resourceUploading ? "Uploading…" : "Upload File"}
              </button>
              {form.resource_file_id && (
                <span className="inline-flex items-center gap-2 font-sans text-sm text-charcoal/70">
                  <FileText className="w-4 h-4 text-forest" strokeWidth={1.6} />
                  {form.resource_filename || "File uploaded"}
                  <button
                    type="button"
                    data-testid="remove-resource-button"
                    onClick={() => setForm((f) => ({ ...f, resource_file_id: null, resource_filename: "" }))}
                    className="text-charcoal/40 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" strokeWidth={1.6} />
                  </button>
                </span>
              )}
              <input
                ref={resourceRef}
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt"
                onChange={handleResourceUpload}
                className="hidden"
                data-testid="resource-file-input"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-4 pt-4 border-t border-charcoal/10">
          <button
            data-testid="save-draft-button"
            onClick={() => save("draft")}
            disabled={saving}
            className="font-sans text-xs tracking-[0.14em] uppercase font-bold px-7 py-4 bg-canvas text-charcoal border border-charcoal/25 hover:border-charcoal transition-colors disabled:opacity-60"
          >
            Save as Draft
          </button>
          <button
            data-testid="publish-button"
            onClick={() => save("published")}
            disabled={saving}
            className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.14em] uppercase font-bold px-7 py-4 bg-forest text-canvas border border-forest hover:bg-charcoal hover:border-charcoal transition-colors disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Publish
          </button>
        </div>
      </main>
    </div>
  );
}
